import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Phone, Coins, Receipt, ShieldCheck, ShieldAlert } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue
} from "@/components/ui/select";
import CompleteIdentificationDialog from "@/components/CompleteIdentificationDialog";

const STATUS_TONE = {
  active: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  inactive: "bg-slate-200 text-slate-700 hover:bg-slate-200",
  blocked: "bg-red-100 text-red-800 hover:bg-red-100",
};

const LEDGER_TONE = {
  EARN: "text-emerald-700",
  REDEEM: "text-primary",
  REFUND_REVERSAL: "text-amber-700",
  MANUAL_ADJUSTMENT: "text-secondary",
  CORRECTION: "text-secondary",
};

export default function CustomerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = ["platform_owner", "vendor_owner", "admin"].includes(user?.role);
  const [profile, setProfile] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [sales, setSales] = useState([]);
  const [busy, setBusy] = useState(true);
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("active");
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustVal, setAdjustVal] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [identOpen, setIdentOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [p, l, s] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/loyalty`),
        api.get(`/customers/${id}/sales`),
      ]);
      setProfile(p.data); setLedger(l.data.ledger || []); setSales(s.data);
      setNewStatus(p.data.status || "active");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load profile");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveStatus = async () => {
    try {
      await api.patch(`/customers/${id}`, { status: newStatus });
      toast.success("Status updated");
      setStatusOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitAdjustment = async () => {
    const v = parseInt(adjustVal, 10);
    if (!v || isNaN(v)) return toast.error("Enter a positive or negative number");
    if (!adjustReason.trim()) return toast.error("Reason is required");
    try {
      await api.post(`/customers/${id}/loyalty/adjust`, { points_change: v, reason: adjustReason.trim() });
      toast.success("Manual adjustment posted");
      setAdjustOpen(false); setAdjustVal(""); setAdjustReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (busy && !profile) return <div className="p-8 text-sm text-muted-foreground">Loading profile…</div>;
  if (!profile) return <div className="p-8 text-sm text-muted-foreground">Customer not found.</div>;

  const cfg = profile.loyalty_config || {};

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="customer-profile-page">
      <div className="flex items-center gap-3">
        <Link to="/customers" className="text-muted-foreground hover:text-foreground text-sm inline-flex items-center gap-1" data-testid="back-to-customers">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Members
        </Link>
      </div>

      <div className="bg-white rounded-lg border p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl md:text-3xl" data-testid="profile-name">
              {profile.first_name} {profile.surname}
            </h1>
            <div className="text-xs font-mono text-muted-foreground mt-1 flex items-center gap-1">
              <Phone className="w-3 h-3" strokeWidth={1.5} />
              <span data-testid="profile-phone">{profile.phone_normalised || profile.phone_original}</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">
              Member ID <span className="font-mono text-foreground/70 normal-case tracking-normal" data-testid="profile-id">{profile.id}</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
              Member since <span className="normal-case tracking-normal text-foreground/70">{new Date(profile.created_at).toLocaleDateString("en-ZA")}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`text-[10px] ${STATUS_TONE[profile.status] || STATUS_TONE.active}`} data-testid="profile-status">
              {String(profile.status || "active").toUpperCase()}
            </Badge>
            {isAdmin && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatusOpen(true)} data-testid="change-status-btn">Change status</Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
          <Metric label="Points balance" value={`${profile.loyalty_balance || 0}`} sub={currency(profile.loyalty_value_rand || 0)} testid="metric-balance" />
          <Metric label="Lifetime earned" value={`${profile.lifetime_earned || 0}`} testid="metric-earned" />
          <Metric label="Lifetime redeemed" value={`${profile.lifetime_redeemed || 0}`} testid="metric-redeemed" />
          <Metric label="Lifetime spend" value={currency(profile.lifetime_spend || 0)} sub={`${profile.transactions_count || 0} txns`} testid="metric-spend" />
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
          {profile.verification?.age_confirmed_18_plus ? (
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><ShieldCheck className="w-3 h-3 mr-1" strokeWidth={1.5} />18+ confirmed</Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><ShieldAlert className="w-3 h-3 mr-1" strokeWidth={1.5} />18+ not confirmed</Badge>
          )}
          {profile.identification_required ? (
            <>
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="ident-required-badge">
                <ShieldAlert className="w-3 h-3 mr-1" strokeWidth={1.5} />Identification required
              </Badge>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setIdentOpen(true)} data-testid="complete-ident-btn">
                Complete identification
              </Button>
            </>
          ) : (
            <span className="text-muted-foreground" data-testid="ident-summary">
              {profile.verification?.id_type} recorded
              {isAdmin && profile.verification?.id_masked && <> · <span className="font-mono">{profile.verification.id_masked}</span></>}
              {isAdmin && profile.verification?.id_issuing_country && <> · {profile.verification.id_issuing_country}</>}
            </span>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" className="rounded-full ml-auto" onClick={() => setAdjustOpen(true)} data-testid="manual-adjust-btn">Manual adjustment</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b flex items-center gap-2">
            <Receipt className="w-4 h-4 text-primary" strokeWidth={1.5} />
            <h2 className="font-display text-lg">Transactions</h2>
          </div>
          {sales.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No transactions yet.</div>
          ) : (
            <div className="divide-y" data-testid="profile-transactions">
              {sales.map((s) => (
                <div key={s.id} className="p-3 flex justify-between items-center text-xs">
                  <div>
                    <div className="font-mono">{s.receipt_no}</div>
                    <div className="text-muted-foreground">{new Date(s.at).toLocaleString("en-ZA")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-semibold">{currency(s.total)}</div>
                    <div className="text-[10px] text-emerald-700">+{s.loyalty_points_earned || 0} pts</div>
                    {s.loyalty_points_redeemed > 0 && <div className="text-[10px] text-primary">−{s.loyalty_points_redeemed} redeemed</div>}
                    {s.status === "voided" && <Badge className="mt-1 text-[10px] bg-red-100 text-red-800 hover:bg-red-100">VOIDED</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b flex items-center gap-2">
            <Coins className="w-4 h-4 text-primary" strokeWidth={1.5} />
            <h2 className="font-display text-lg">Loyalty ledger</h2>
          </div>
          {ledger.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No loyalty activity yet.</div>
          ) : (
            <div className="divide-y" data-testid="profile-ledger">
              {ledger.map((e) => (
                <div key={e.id} className="p-3 flex justify-between items-center text-xs">
                  <div>
                    <div className={`font-medium ${LEDGER_TONE[e.type] || "text-foreground"}`}>{e.type.replace("_", " ")}</div>
                    <div className="text-muted-foreground">{new Date(e.at).toLocaleString("en-ZA")}</div>
                    {e.notes && <div className="text-[10px] text-muted-foreground">{e.notes}</div>}
                  </div>
                  <div className="text-right">
                    <div className={`font-mono font-semibold ${e.points_change >= 0 ? "text-emerald-700" : "text-primary"}`}>
                      {e.points_change >= 0 ? "+" : ""}{e.points_change}
                    </div>
                    <div className="text-[10px] text-muted-foreground">bal {e.balance_after}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Change status</DialogTitle></DialogHeader>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStatusOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={saveStatus} data-testid="status-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manual loyalty adjustment</DialogTitle>
            <DialogDescription>
              Creates a MANUAL_ADJUSTMENT ledger entry. Use a negative value to remove points. Balance is never overwritten.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Points change</label>
              <Input type="number" value={adjustVal} onChange={(e) => setAdjustVal(e.target.value)} placeholder="e.g. 10 or -5" data-testid="adjust-value" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Reason</label>
              <Textarea value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} rows={2} data-testid="adjust-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdjustOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={submitAdjustment} data-testid="adjust-submit-btn">Post adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CompleteIdentificationDialog customerId={id} open={identOpen} onOpenChange={setIdentOpen} onSaved={load} />
    </div>
  );
}

function Metric({ label, value, sub, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono text-2xl tabular-nums font-semibold mt-1" data-testid={testid}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
